import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { format, startOfDay, differenceInDays, isToday, isThisMonth, isThisYear, startOfWeek, addDays, isSameDay } from 'date-fns';
import { FiX, FiTrendingUp, FiCheckCircle, FiCalendar, FiEdit3 } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

export default function Analytics({ onClose, entries }) {
  const analytics = useMemo(() => {
    if (!entries || entries.length === 0) {
      return {
        currentStreak: 0,
        longestStreak: 0,
        totalEntries: 0,
        totalTasks: 0,
        completedTasks: 0,
        thisMonthEntries: 0,
        thisMonthTasks: 0,
        thisYearEntries: 0,
        todayEntries: 0,
        weeklyActivity: Array(7).fill(false),
      };
    }

    // Calculate streaks
    const sortedDates = entries
      .map(e => startOfDay(e.timestamp))
      .sort((a, b) => b - a);

    const uniqueDates = [...new Set(sortedDates.map(d => d.getTime()))].map(t => new Date(t));

    let currentStreak = 0;
    let longestStreak = 0;
    let tempStreak = 0;

    const today = startOfDay(new Date());
    let checkDate = today;

    // Calculate current streak
    for (const date of uniqueDates) {
      const daysDiff = differenceInDays(checkDate, date);
      if (daysDiff === 0) {
        currentStreak++;
        checkDate = new Date(checkDate.getTime() - 24 * 60 * 60 * 1000);
      } else if (daysDiff === 1) {
        currentStreak++;
        checkDate = date;
      } else {
        break;
      }
    }

    // Calculate longest streak
    for (let i = 0; i < uniqueDates.length; i++) {
      if (i === 0) {
        tempStreak = 1;
      } else {
        const daysDiff = differenceInDays(uniqueDates[i - 1], uniqueDates[i]);
        if (daysDiff === 1) {
          tempStreak++;
        } else {
          longestStreak = Math.max(longestStreak, tempStreak);
          tempStreak = 1;
        }
      }
    }
    longestStreak = Math.max(longestStreak, tempStreak);

    // Calculate weekly activity (last 7 days)
    const weekStart = startOfWeek(new Date(), { weekStartsOn: 0 }); // Sunday
    const weeklyActivity = Array(7).fill(false).map((_, index) => {
      const day = addDays(weekStart, index);
      return uniqueDates.some(date => isSameDay(date, day));
    });

    // Calculate task statistics
    const totalTasks = entries.filter(e => e.type === 'todo').length;
    const completedTasks = entries.filter(e => e.type === 'todo' && e.completed).length;
    const thisMonthEntries = entries.filter(e => isThisMonth(e.timestamp)).length;
    const thisMonthTasks = entries.filter(e => e.type === 'todo' && isThisMonth(e.timestamp)).length;
    const thisYearEntries = entries.filter(e => isThisYear(e.timestamp)).length;
    const todayEntries = entries.filter(e => isToday(e.timestamp)).length;

    return {
      currentStreak,
      longestStreak,
      totalEntries: entries.length,
      totalTasks,
      completedTasks,
      thisMonthEntries,
      thisMonthTasks,
      thisYearEntries,
      todayEntries,
      weeklyActivity,
    };
  }, [entries]);

  const completionRate = analytics.totalTasks > 0
    ? Math.round((analytics.completedTasks / analytics.totalTasks) * 100)
    : 0;

  const weekDays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-[#0a0a0a] z-50 overflow-y-auto pb-safe"
    >
      {/* Header */}
      <div className="sticky top-0 bg-[#0a0a0a]/95 backdrop-blur-lg border-b border-[#262626] z-10">
        <div className="w-full max-w-[800px] mx-auto px-4 py-3 flex items-center justify-between">
          <h2 className="text-lg font-bold text-white">Analytics</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-[#262626] transition-colors"
          >
            <SafeIcon icon={FiX} className="text-lg text-[#a3a3a3]" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="w-full max-w-[800px] mx-auto px-4 pt-4 pb-32 md:pt-6 md:pb-8 space-y-4 md:space-y-5">

        {/* Streak Section - Compact layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
          {/* Current Streak */}
          <div className="bg-[#1a1a1a] border border-[#262626] rounded-lg p-4">
            <div className="text-xs uppercase tracking-wide text-[#737373] mb-2">Activity Streak</div>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-4xl md:text-5xl font-bold text-white">{analytics.currentStreak}</div>
                <div className="text-xs text-[#a3a3a3] mt-1">day streak</div>
              </div>
              <div className="text-right">
                <div className="text-xs text-[#737373]">Longest</div>
                <div className="text-2xl font-bold text-white">{analytics.longestStreak}</div>
              </div>
            </div>
          </div>

          {/* Weekly Activity Visual */}
          <div className="bg-[#1a1a1a] border border-[#262626] rounded-lg p-4">
            <div className="text-xs uppercase tracking-wide text-[#737373] mb-2">This Week</div>
            <div className="flex justify-between items-center gap-1">
              {weekDays.map((day, index) => (
                <div key={index} className="flex flex-col items-center gap-1.5">
                  <div className="text-[10px] text-[#737373]">{day}</div>
                  <div
                    className={`w-7 h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center text-xs transition-colors ${
                      analytics.weeklyActivity[index]
                        ? 'bg-[#3b82f6] text-white'
                        : 'bg-[#0a0a0a] border border-[#404040] text-[#404040]'
                    }`}
                  >
                    {analytics.weeklyActivity[index] && '✓'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Main Grid - 2 columns on desktop */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-4">

          {/* Left Column - Entries */}
          <div>
            <h3 className="text-xs uppercase tracking-wide font-medium text-[#737373] mb-2">Entries</h3>
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-[#1a1a1a] border border-[#262626] rounded-lg p-3 min-h-[72px] flex flex-col justify-center">
                <div className="flex items-center gap-2 mb-1">
                  <SafeIcon icon={FiEdit3} className="text-sm text-[#3b82f6]" />
                  <span className="text-[10px] text-[#737373]">TODAY</span>
                </div>
                <div className="text-2xl font-bold text-white">{analytics.todayEntries}</div>
              </div>
              <div className="bg-[#1a1a1a] border border-[#262626] rounded-lg p-3 min-h-[72px] flex flex-col justify-center">
                <div className="flex items-center gap-2 mb-1">
                  <SafeIcon icon={FiCalendar} className="text-sm text-[#3b82f6]" />
                  <span className="text-[10px] text-[#737373]">MONTH</span>
                </div>
                <div className="text-2xl font-bold text-white">{analytics.thisMonthEntries}</div>
              </div>
              <div className="bg-[#1a1a1a] border border-[#262626] rounded-lg p-3 min-h-[72px] flex flex-col justify-center">
                <div className="flex items-center gap-2 mb-1">
                  <SafeIcon icon={FiCalendar} className="text-sm text-[#3b82f6]" />
                  <span className="text-[10px] text-[#737373]">YEAR</span>
                </div>
                <div className="text-2xl font-bold text-white">{analytics.thisYearEntries}</div>
              </div>
              <div className="bg-[#1a1a1a] border border-[#262626] rounded-lg p-3 min-h-[72px] flex flex-col justify-center">
                <div className="flex items-center gap-2 mb-1">
                  <SafeIcon icon={FiTrendingUp} className="text-sm text-[#3b82f6]" />
                  <span className="text-[10px] text-[#737373]">ALL TIME</span>
                </div>
                <div className="text-2xl font-bold text-white">{analytics.totalEntries}</div>
              </div>
            </div>
          </div>

          {/* Right Column - Tasks */}
          <div>
            <h3 className="text-xs uppercase tracking-wide font-medium text-[#737373] mb-2">Tasks</h3>

            {/* Completion Rate with Progress Bar */}
            <div className="bg-[#1a1a1a] border border-[#262626] rounded-lg p-3 mb-2 min-h-[72px] flex flex-col justify-center">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] text-[#737373] uppercase">Completion</span>
                <span className="text-xl font-bold text-white">{completionRate}%</span>
              </div>
              <div className="w-full bg-[#0a0a0a] rounded-full h-1.5 overflow-hidden">
                <div
                  className="bg-[#3b82f6] h-full rounded-full transition-all duration-500"
                  style={{ width: `${completionRate}%` }}
                />
              </div>
              <div className="text-[10px] text-[#737373] mt-1.5">
                {analytics.completedTasks} of {analytics.totalTasks} completed
              </div>
            </div>

            {/* Task Stats Grid */}
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-[#1a1a1a] border border-[#262626] rounded-lg p-3 min-h-[72px] flex flex-col justify-center">
                <div className="flex items-center gap-2 mb-1">
                  <SafeIcon icon={FiCheckCircle} className="text-sm text-[#10b981]" />
                  <span className="text-[10px] text-[#737373]">DONE</span>
                </div>
                <div className="text-2xl font-bold text-white">{analytics.completedTasks}</div>
              </div>
              <div className="bg-[#1a1a1a] border border-[#262626] rounded-lg p-3 min-h-[72px] flex flex-col justify-center">
                <div className="flex items-center gap-2 mb-1">
                  <SafeIcon icon={FiCalendar} className="text-sm text-[#f97316]" />
                  <span className="text-[10px] text-[#737373]">ACTIVE</span>
                </div>
                <div className="text-2xl font-bold text-white">{analytics.totalTasks - analytics.completedTasks}</div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </motion.div>
  );
}